import { ComponentProps } from 'react';
import {
    Badge,
    BadgeProps,
    HStack,
    Popover,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { ReportedFindingData, ReportedFindingStatus } from '@acp/shared';
import { faCircle, faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { FaIcon, PopoverTag } from '@acp/core-ui';

function ReportedFindingTag({
    reportedFinding,
    popoverPlacement = 'auto',
    path,
    ...props
}: {
    reportedFinding: ReportedFindingData;
    popoverPlacement?: ComponentProps<typeof Popover>['placement'];
    path?: string;
} & BadgeProps) {
    // we only show the highest category
    const highestCategory = reportedFinding?.servicingCategory;

    if (highestCategory == null) {
        return null;
    }

    if (
        reportedFinding.problemRegister.status === ReportedFindingStatus.RESOLVED
        || reportedFinding.problemRegister.status === ReportedFindingStatus.RESOLVED_BY_THIRD_PARTY
    ) {
        return (
            <Badge cursor="pointer" userSelect="none" colorScheme="success" {...props}>
                Resolved
            </Badge>
        );
    }

    if (reportedFinding.problemRegister.status === ReportedFindingStatus.REVIEW_REQUIRED) {
        return (
            <PopoverTag
                colorScheme="info"
                popoverPlacement={popoverPlacement}
                title={<Text>Under review</Text>}
            >
                <Text fontSize="small" fontWeight="bold">
                    Under review
                </Text>
                <Text fontSize="small">
                    Our team is currently reviewing the documentation provided
                </Text>
            </PopoverTag>
        );
    }

    return (
        <PopoverTag
            popoverPlacement={popoverPlacement}
            colorScheme="blackAlpha"
            title={(
                <HStack spacing={1}>
                    <FaIcon icon={faCircle} color={highestCategory.color} fontSize="2xs" />
                    <Text>{highestCategory.name}</Text>
                </HStack>
            )}
        >
            <Text fontSize="small" fontWeight="bold">
                {highestCategory.name}
            </Text>
            <Text
                fontSize="small"
                style={{
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word', // Fallback for older browsers
                    whiteSpace: 'normal',
                }}
                dangerouslySetInnerHTML={{
                    __html: highestCategory.description.replace(/(<([^>]+)>)/gi, ''),
                }}
            />
            {path != null && path !== '' && (
                <Link to={path}>
                    <Text
                        fontSize="small"
                        fontWeight="bold"
                        textDecoration="underline"
                        _hover={{ textDecoration: 'none' }}
                        mt={1}
                    >
                        View reported findings
                        <FaIcon icon={faUpRightFromSquare} ml={1} h={3} />
                    </Text>
                </Link>
            )}
        </PopoverTag>
    );
}

function ReportedFindingTagCollection({
    reportedFindings,
    popoverPlacement = 'auto',
}: {
    reportedFindings: ReportedFindingData[];
    popoverPlacement?: ComponentProps<typeof Popover>['placement'];
}) {
    return (
        <VStack alignItems="flex-start" gap={2} wrap="wrap">
            <ReportedFindingTag
                reportedFinding={reportedFindings[0]}
                popoverPlacement={popoverPlacement}
            />
        </VStack>
    );
}

export { ReportedFindingTagCollection, ReportedFindingTag };
